import { ApiStatContext } from 'react-scale/components/ExpertManagement/stores/statContext.store';
import apiFetch, { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';

function getContextQuery(context: ApiStatContext) {
  return (
    `lookbackType=${context.dateRangeOption.lookbackType}` +
    `&lookbackPeriod=${context.dateRangeOption.lookbackPeriod}` +
    (context.projectId ? `&projectId=${context.projectId}` : '')
  );
}

export async function fetchGlobalStats(context: ApiStatContext) {
  const result = await apiFetch(
    null,
    `internal/expert-management/globalStats?${getContextQuery(context)}&podTypeFilter=${
      context.podTypeFilter
    }`,
    {
      method: 'GET',
    },
  );
  return result;
}

export async function fetchCommunityGlobalStats() {
  const result = await apiFetch(null, `internal/expert-management/communityGlobalStats`, {
    method: 'GET',
  });
  return result.data as Record<string, number>;
}

export async function fetchManagerAggregationDataShort(context: ApiStatContext) {
  // TODO HACK(albert): Fetches the userInfo columns first. This should probably be
  // generalized in a cleaner way in the future, possibly specifying the columns
  // directly in the context? Decide later.
  const result = await apiFetch(
    null,
    `internal/expert-management/globalManagerStatsShort?${getContextQuery(context)}&podTypeFilter=${
      context.podTypeFilter
    }`,
    {
      method: 'GET',
    },
  );
  return result.data;
}

export async function fetchManagerAggregationData(context: ApiStatContext) {
  const result = await apiFetch(
    null,
    `internal/expert-management/globalManagerStats?${getContextQuery(context)}&podTypeFilter=${
      context.podTypeFilter
    }`,
    {
      method: 'GET',
    },
  );
  return result.data;
}

export async function fetchManagerHierarchyData() {
  const result = await apiFetch(null, `internal/expert-management/globalManagerHierarchy`, {
    method: 'GET',
  });
  return result.data as Record<string, object[]>;
}

export async function fetchManagerLastAttemptedProjects(managerId: string) {
  const result = await apiFetch(
    null,
    `internal/expert-management/managerLastAttemptedProjects?managerId=${managerId}`,
    {
      method: 'GET',
    },
  );
  return result.data as any[];
}

export async function fetchStatsForExpert(context: ApiStatContext, expertId: string) {
  const result = await apiFetch(
    null,
    `internal/expert-management/statsForExpert?expertId=${expertId}&${getContextQuery(context)}`,
    { method: 'GET' },
  );
  return result;
}

export async function fetchLastAttemptsForExperts(context: ApiStatContext, expertIds: string[]) {
  const result = await apiFetch(null, `internal/expert-management/lastAttemptsForExperts`, {
    method: 'POST',
    body: { expertIds },
  });
  return result;
}

export async function fetchDolphinTasksForExpert(context: ApiStatContext, expertId: string) {
  const result = await apiFetch(
    null,
    `internal/expert-management/dolphinTasksForExpert?expertId=${expertId}&${getContextQuery(
      context,
    )}`,
    { method: 'GET' },
  );
  return result;
}

export async function fetchStatsForManager(
  context: ApiStatContext,
  managerId: string,
  squadId: string,
) {
  const result = await apiFetch(
    null,
    `internal/expert-management/statsForManager?managerId=${managerId}&squadId=${squadId}&${getContextQuery(
      context,
    )}`,
    { method: 'GET' },
  );
  return result;
}

export async function fetchUserInfo(userId: string) {
  const result = await apiFetch(null, `internal/expert-management/userInfo?userId=${userId}`, {
    method: 'GET',
  });
  return result.data;
}

export async function fetchExpertEmailsForManager(managerId: string) {
  const result = await apiFetch(
    null,
    `internal/expert-management/expert-emails?managerId=${managerId}`,
    {
      method: 'GET',
    },
  );
  return result.data;
}

export async function fetchWorkerManagers(podTypeFilter: string) {
  const result = await apiFetch(
    null,
    `internal/expert-management/listWorkerManagers${
      podTypeFilter.length ? `?podTypeFilter=${podTypeFilter}` : ''
    }`,
    {
      method: 'GET',
    },
  );
  return result.managers;
}

export async function fetchWorkersManagers(workerIds: string[]) {
  const result = await apiFetch(null, `internal/expert-management/listWorkersManagers`, {
    method: 'POST',
    body: { workerIds },
  });
  return result;
}

// ===== COMMENTS =====

export async function fetchCommentsForExpert(expertId: string) {
  const result = await apiFetch(
    null,
    `internal/expert-management/commentsForExpert?expertId=${expertId}`,
    {
      method: 'GET',
    },
  );
  return result;
}

export async function addCommentForExpert(expertId: string, comment: string) {
  const result = await apiFetch(null, `internal/expert-management/addCommentForExpert`, {
    method: 'POST',
    body: { expertId, comment },
  });
  return result;
}

export async function removeCommentById(expertId: string, commentId: string) {
  const result = await apiFetch(null, `internal/expert-management/removeCommentForExpert`, {
    method: 'POST',
    body: { expertId, commentId },
  });
  return result;
}

// ===== USER MANAGEMENT =====

export async function setManagerForWorkers(managerId: string | null, workerIds: string[]) {
  const result = await apiFetch(null, `internal/expert-management/setManagerForWorkers`, {
    method: 'POST',
    body: {
      managerId,
      workerIds,
    },
  });
  return result;
}

export async function promoteToTopLevelManager(
  managerId: string,
  podType?: string,
  slackConfig?: any,
) {
  const result = await apiFetch(null, `internal/expert-management/setManagerRole`, {
    method: 'POST',
    body: {
      userId: managerId,
      isTopLevelManager: true,
      podType,
      slackConfig,
    },
  });
  return result;
}

export async function removeWorkerEntries(workerIds: string[]) {
  const result = await apiFetch(null, `internal/expert-management/removeWorkerEntries`, {
    method: 'POST',
    body: {
      workerIds,
    },
  });
  if (!result.success) {
    defaultApiFetchErrorHandler({ name: 'error', message: result.error, logError: () => null });
  }
  return result;
}

export async function rerunQueueForWorker(workerId: string) {
  const result = await apiFetch(null, `internal/expert-management/rerun_queue`, {
    method: 'POST',
    body: {
      workerId,
    },
  });
  return result;
}

export async function fetchPodTypes() {
  const result = await apiFetch(null, `internal/expert-management/podTypes`, {
    method: 'GET',
  });
  return result;
}

export async function fetchTeamChangeLog(managerId: string) {
  const result = await apiFetch(
    null,
    `internal/expert-management/teamChangeLog?managerId=${managerId}`,
    {
      method: 'GET',
    },
  );
  return result;
}

export async function fetchSquads(podTypeFilter?: string) {
  const result = await apiFetch(
    null,
    `internal/expert-management/squads${podTypeFilter ? '?podTypeFilter=' + podTypeFilter : ''}`,
    {
      method: 'GET',
    },
  );
  return result.squads;
}

export async function fetchSquadById(squadId: string) {
  const result = await apiFetch(null, `internal/expert-management/squad/${squadId}`, {
    method: 'GET',
  });
  return result.squad;
}
